/** @format */

import {Table, Toast} from '@sttark/design-system';
import React, {useState, useEffect} from 'react';
import {useFetch, If} from 'frontier-utilities';
import {ActionColumn} from './action_column';
import {NameColumn} from './name_column';

/**
 * Show the selected address if closed
 * Otherwise show a list of active addresses with the currently selected address marked
 */

// styles
import './addresses_list.scss';

export const AddressesList = props => {
    const [{addresses, states, countries}, setAddresses] = React.useState({
        addresses: [],
        states: {},
        countries: {}
    });
    const [filteredAddresses, setFilteredAddresses] = useState([]);

    const [toast, setToast] = useState({
        isDanger: false,
        isOpen: false,
        message: ''
    });

    const {isLoading, fetchData} = useFetch(
        `/account/addresses/index?is_shipping_address=${props.isShippingAddress}`,
        ({addresses, states, countries}) => {
            setAddresses({addresses, states, countries});
            setFilteredAddresses(addresses);
        }
    );

    const sortDefaultFirst = (a, b) => {
        // Prioritize default addresses
        if (a.is_default_address && !b.is_default_address) return -1;
        if (b.is_default_address && !a.is_default_address) return 1;

        // Compare full names, handling potential undefined/null values
        const fullNameA = a.full_name || '';
        const fullNameB = b.full_name || '';
        if (fullNameA < fullNameB) return -1;
        if (fullNameA > fullNameB) return 1;

        // Compare companies, handling potential undefined/null values
        const companyA = a.company || '';
        const companyB = b.company || '';
        if (companyA < companyB) return -1;
        if (companyA > companyB) return 1;

        // If all other criteria are equal, consider the items equal
        return 0;
    };

    const hideToast = () => {
        setToast({
            isDanger: false,
            isOpen: false,
            message: ''
        });
    };

    function onSuccessfulDelete() {
        setToast({
            message: 'Address removed successfully',
            isDanger: true,
            isOpen: true
        });

        fetchData();
    }
    function onSuccessfulSave(message) {
        setToast({
            isDanger: false,
            isOpen: true,
            message
        });
    }

    // if the parent passes a filterableValue, we need to filter the addresses
    useEffect(() => {
        if (props.filterableValue === '') {
            setFilteredAddresses(addresses);
            return;
        }

        if (!props.filterableValue) return;

        if (props.filterableKey?.toLowerCase() === 'address') {
            const filteredAddresses = addresses.filter(address => {
                return (
                    address.address1 +
                    address.address2 +
                    address.city +
                    address.state +
                    address.country
                )
                    .toLowerCase()
                    .replaceAll('-', '')
                    .includes(props.filterableValue?.toLowerCase()?.replaceAll('-', ''));
            });

            setFilteredAddresses(filteredAddresses);
            return;
        }
        const filteredAddresses = addresses.filter(address => {
            return address[props.filterableKey?.toLowerCase()]
                .toLowerCase()
                .replaceAll('-', '')
                .includes(props.filterableValue?.toLowerCase()?.replaceAll('-', ''));
        });

        setFilteredAddresses(filteredAddresses);
    }, [props.filterableValue, props.filterableKey]);

    // allows re-fetch from the parent. Value must be of type hum greater than 0
    useEffect(() => {
        if (!(props.refetch > 0)) return;

        fetchData();
    }, [props.refetch]);

    if (isLoading) {
        <div className="alert alert-info">Loading Addresses...</div>;
    }

    if (!addresses.length) {
        return (
            <aside className="alert alert-info" role="alert">
                You have no addresses saved. You can add a new address at any time.
            </aside>
        );
    }
    return (
        <>
            <Toast
                danger={toast.isDanger}
                autoHideDuration={4000}
                isOpen={toast.isOpen}
                onClose={hideToast}
                zIndex={3001}
                autoHide>
                <p className="color-structure-white m-0">{toast.message}</p>
            </Toast>
            <div className="addresses-list-05vn">
                <Table
                    columns={[
                        {
                            title: 'Name / Company',
                            index: 'name',
                            key: 'name'
                        },
                        {
                            index: 'address',
                            title: 'Address',
                            key: 'address'
                        },
                        {
                            title: 'Contact',
                            index: 'contact',
                            key: 'contact'
                        },
                        {
                            index: 'action',
                            key: 'action',
                            title: ''
                        }
                    ]}
                    rows={[...filteredAddresses].sort(sortDefaultFirst).map(address => {
                        return {
                            name: (
                                <NameColumn
                                    address={address}
                                    onSetDefaultComplete={message => {
                                        onSuccessfulSave(message);
                                        fetchData();
                                    }}
                                />
                            ),
                            address: (
                                <div className="addresses-list-05vn__address">
                                    <p className="m-0">{address.address1}</p>
                                    <p className="m-0">{address.address2}</p>
                                    <p className="m-0">
                                        {address.city}, {address.state} {address.zip}
                                    </p>
                                    <p className="m-0">{address.country}</p>
                                </div>
                            ),
                            contact: (
                                <div className="addresses-list-05vn__contact">
                                    <p className="m-0">
                                        <b>Phone:</b> {address.phone}
                                    </p>
                                    <p className="m-0">
                                        <b>Contact Email:</b> {address.email}
                                    </p>
                                    <If condition={props.isShippingAddress}>
                                        <p className="m-0">
                                            <b>Proofing Email:</b> {address.proof_email || 'N/A'}
                                        </p>
                                    </If>
                                </div>
                            ),
                            action: (
                                <ActionColumn
                                    isShippingAddress={props.isShippingAddress}
                                    onDeleteComplete={onSuccessfulDelete}
                                    onSaveComplete={() => {
                                        onSuccessfulSave('Address saved successfully');
                                        fetchData();
                                    }}
                                    countries={countries}
                                    address={address}
                                    states={states}
                                />
                            ),
                            key: address.id
                        };
                    })}
                />
            </div>
        </>
    );
};
